import React, { useEffect, useState, useImperativeHandle, forwardRef } from 'react';
import { Label } from '../input-text/input.styled';
import Editor, { Toolbar, BtnBold, BtnItalic, BtnUnderline } from 'react-simple-wysiwyg';
import { EditorContainer } from './richTextEditor.styled';
import { ActionButton } from '../vertical-dot/popper';
import { rem } from '../../utils/style';

interface ExtendedRichType {
  name: string;
  onChange: (value: string) => void;
  value: string;
  error?: any;
  touched?: any;
  initialContentState?: any;
  label?: string;
  onEditorRef?: (ref: any) => void;
  variables?: string[];
  disabled?: boolean;
  variableTypes?: boolean;
}

export interface EditorRef {
  insertText: (textToInsert: string) => void;
}

const ExtendedRichTextEditor = forwardRef<EditorRef, ExtendedRichType>(
  (
    {
      onChange,
      value,
      error,
      touched,
      label,
      variables,
      disabled,
      variableTypes,
    }: ExtendedRichType,
    ref
  ): JSX.Element => {
    const [html, setHtml] = useState<string>(value || '');
    const editorRef = React.useRef<HTMLDivElement>(null);
    const savedSelectionRef = React.useRef<Range | null>(null);

    // Save current selection when editor is focused
    const handleEditorFocus = () => {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        savedSelectionRef.current = selection.getRangeAt(0).cloneRange();
      }
    };

    // Function to insert text at cursor position
    const insertText = (textToInsert: string) => {
      if (!editorRef.current) return;

      let selectionRange = null;

      // First try to use current selection
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        selectionRange = selection.getRangeAt(0);

        // Check if selection is inside editor
        const isInsideEditor = editorRef.current.contains(selectionRange.commonAncestorContainer);
        if (!isInsideEditor) {
          selectionRange = null; // Current selection is not in editor
        }
      }

      // If no valid current selection, try to use saved selection
      if (!selectionRange && savedSelectionRef.current) {
        selectionRange = savedSelectionRef.current;

        // Focus the editor
        if (editorRef.current) {
          editorRef.current.focus();

          // Restore the saved selection
          const newSelection = window.getSelection();
          if (newSelection) {
            newSelection.removeAllRanges();
            newSelection.addRange(selectionRange);
          }
        }
      }

      // If we have a valid selection, insert at that position
      if (selectionRange) {
        // Insert text at current position
        selectionRange.deleteContents();
        selectionRange.insertNode(document.createTextNode(textToInsert));

        // Move cursor after inserted text
        selectionRange.setStartAfter(selectionRange.endContainer);
        selectionRange.collapse(true);

        if (selection) {
          selection.removeAllRanges();
          selection.addRange(selectionRange);
        }

        // Update state with the new HTML content
        if (editorRef.current) {
          const newHtml = editorRef.current.innerHTML;
          setHtml(newHtml);
          onChange(newHtml);
        }
      } else {
        // No valid selection, append to end
        setHtml((prevHtml) => prevHtml + textToInsert);
        onChange(html + textToInsert);
      }

      // Save the new selection after insertion
      const newSelection = window.getSelection();
      if (newSelection && newSelection.rangeCount > 0) {
        savedSelectionRef.current = newSelection.getRangeAt(0).cloneRange();
      }
    };

    // Expose the insertText method via ref
    useImperativeHandle(ref, () => ({
      insertText,
    }));

    const handleChange = (e: any) => {
      const newHtml = e.target.value!;
      setHtml(newHtml);
      onChange(newHtml);

      // Save selection after content change
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        savedSelectionRef.current = selection.getRangeAt(0).cloneRange();
      }
    };

    // Update state when value prop changes
    useEffect(() => {
      if (value !== undefined && value !== html) {
        setHtml(value);
      }
    }, [value]);

    return (
      <div>
        <EditorContainer>
          {label && <Label>{label || 'Description'}</Label>}
          <div
            className={`${Boolean(error) && Boolean(touched) ? 'border border-red-700' : ''}`}
            style={{ minHeight: '38rem' }}>
            <Editor
              ref={editorRef}
              value={html}
              onChange={handleChange}
              onFocus={handleEditorFocus}
              onBlur={() => {
                // Save selection on blur to capture cursor position
                const selection = window.getSelection();
                if (selection && selection.rangeCount > 0) {
                  savedSelectionRef.current = selection.getRangeAt(0).cloneRange();
                }
              }}
              disabled={disabled}
              containerProps={{
                className: 'editorClassName text-[16px]',
                style: { minHeight: rem(495), maxHeight: rem(495) },
              }}>
              <Toolbar>
                <BtnBold />
                <BtnItalic />
                <BtnUnderline />
                {/* <BtnBulletList />
                <BtnNumberedList /> */}
                {variables && variables.length > 0 && (
                  <div className='toolbar-custom mt-2'>
                    <ActionButton
                      as='Button'
                      buttonTitle='Insert Variables'
                      icon='add'
                      hideMenuOnActionClick
                      actions={variables.map((value) => {
                        return {
                          action: value,
                          onClick: () => {
                            // We use setTimeout to ensure this runs after the click event is fully processed
                            setTimeout(() => {
                              if (variableTypes) {
                                insertText(`[${value}]`);
                              } else {
                                insertText(`{{${value}}}`);
                              }
                            }, 0);
                          },
                        };
                      })}
                      doubleTable={true}
                    />
                  </div>
                )}
              </Toolbar>
            </Editor>
          </div>
        </EditorContainer>
        {Boolean(error) && Boolean(touched) && <div className='field-error-message'>{error}</div>}
      </div>
    );
  }
);

export default ExtendedRichTextEditor;
